import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { AlertTriangle, Copy, Send, Undo2 } from "lucide-react"
import type { User } from "@pishgam/contracts"

import { api } from "../api"
import { formatMonthYear, LoadingCard, messageFor, Page } from "../shared"
import { fa, STATE_LABEL, type AllocationState, type TasksResponse } from "../process"
import { ReturnDialog } from "@/components/return-dialog"
import { StatusStepper, type StepperAllocation } from "@/components/status-stepper"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Field, FieldError, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

type Unit = { id: number; nameFa: string }
type AllocationDetail = Omit<StepperAllocation, "lines" | "employee"> & {
  /** "units": a business head who sees only their own businesses' share. */
  view?: "full" | "units"
  /** This person approved a step of it (or is admin) and the month is still open. */
  canReturn?: boolean
  id: number
  jalaliYear: number
  jalaliMonth: number
  notes: string
  rejectionReason?: string
  employee: { id: number; nameFa: string; division?: { nameFa: string; head?: { id: number; nameFa: string } | null } | null }
  lines: Array<StepperAllocation["lines"][number] & { businessUnitId: number }>
  history?: Array<{ id: number; action: string; notes?: string; timestamp: string; actor?: { nameFa: string } | null; businessUnit?: { nameFa: string } | null }>
}
type AllocationSummary = { id: number; jalaliYear: number; jalaliMonth: number; status: string; lines: Array<{ businessUnitId: number; percentage: string }> }

const HISTORY_LABEL: Record<string, string> = {
  submitted: "ارسال شد", resubmitted: "دوباره ارسال شد", div_approved: "تأیید مدیر واحد", div_rejected: "رد توسط مدیر واحد",
  bu_approved: "تأیید مدیر کسب‌وکار", bu_rejected: "رد توسط مدیر کسب‌وکار", finalized: "تأیید نهایی",
  returned: "برگشت برای اصلاح پس از تأیید",
}

/** /allocations/current → opens (or creates) the split for the month being closed. */
export function CurrentAllocation() {
  const navigate = useNavigate()
  const current = useMutation({ mutationFn: () => api<{ id: number }>("/allocations/current", { method: "POST" }), onSuccess: (value) => navigate(`/allocations/${value.id}`, { replace: true }) })
  useEffect(() => { current.mutate() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return <Page title="نسبت کاری من">{current.error ? <p className="text-sm text-destructive">{messageFor(current.error)}</p> : <LoadingCard />}</Page>
}

export function AllocationPage({ user }: { user: User }) {
  const { id } = useParams()
  const client = useQueryClient()
  const allocation = useQuery({ queryKey: ["allocation", id], queryFn: () => api<AllocationDetail>(`/allocations/${id}`), enabled: Boolean(id) })
  const organization = useQuery({ queryKey: ["organization"], queryFn: () => api<{ businessUnits: Unit[] }>("/organization") })
  const mine = useQuery({ queryKey: ["allocations"], queryFn: () => api<AllocationSummary[]>("/allocations") })
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const [notes, setNotes] = useState("")
  const [values, setValues] = useState<Record<number, string>>({})
  const [returning, setReturning] = useState(false)

  useEffect(() => {
    if (!allocation.data) return
    setNotes(allocation.data.notes ?? "")
    setValues(Object.fromEntries(allocation.data.lines.map((line) => [line.businessUnitId, String(+(Number(line.percentage) * 100).toFixed(2))])))
  }, [allocation.data])

  const save = useMutation({
    mutationFn: (submitNow: boolean) => api<void>(`/allocations/${id}/${submitNow ? "submit" : "draft"}`, {
      method: submitNow ? "POST" : "PUT",
      body: JSON.stringify({ notes, lines: Object.entries(values).filter(([, value]) => value !== "").map(([businessUnitId, percentage]) => ({ businessUnitId: Number(businessUnitId), percentage: toLatin(percentage) || "0" })) }),
    }),
    onSuccess: () => Promise.all(["allocation", "allocations", "process-tasks"].map((key) => client.invalidateQueries({ queryKey: [key] }))),
  })

  const previous = useMemo(() => {
    if (!allocation.data || !mine.data) return null
    const ordOf = (item: { jalaliYear: number; jalaliMonth: number }) => item.jalaliYear * 12 + item.jalaliMonth
    return mine.data.filter((item) => ordOf(item) < ordOf(allocation.data!) && item.lines.length).sort((a, b) => ordOf(b) - ordOf(a))[0] ?? null
  }, [allocation.data, mine.data])

  if (!allocation.data || !organization.data) return <Page title="نسبت کاری"><LoadingCard /></Page>
  const data = allocation.data
  const state = data.status as AllocationState
  const owner = data.employee.id === user.id
  const unitsOnly = data.view === "units"
  const editable = owner && (state === "draft" || state === "rejected")
  const total = Object.values(values).reduce((sum, value) => sum + (Number(toLatin(value)) || 0), 0)
  const complete = Math.abs(total - 100) < 0.001
  const cycle = tasks.data?.cycle
  const isCyclePeriod = cycle && cycle.period.year === data.jalaliYear && cycle.period.month === data.jalaliMonth

  return (
    <Page
      title={`نسبت کاری ${formatMonthYear(data.jalaliYear, data.jalaliMonth)}`}
      description={owner ? "سهم وقتی که این ماه برای هر کسب‌وکار گذاشته‌اید را به درصد وارد کنید. جمع باید ۱۰۰٪ شود." : unitsOnly ? `سهم کسب‌وکار شما از وقت ${data.employee.nameFa}` : `نسبت کاری ${data.employee.nameFa}`}
    >
      {state === "rejected" && data.rejectionReason && (
        <div role="alert" className="flex gap-3 rounded-xl border border-destructive/50 bg-destructive/5 p-4">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
          <div>
            <p className="font-medium text-destructive">برگشت خورده — لطفاً اصلاح و دوباره ارسال کنید</p>
            <p className="mt-1 text-sm">{data.rejectionReason}</p>
          </div>
        </div>
      )}
      {editable && isCyclePeriod && (
        <p className={cn("text-sm", cycle.deadlines.allocation.passed ? "text-destructive" : "text-muted-foreground")}>
          {cycle.deadlines.allocation.passed ? `مهلت ثبت (${fa(cycle.deadlines.allocation.label)}) گذشته است؛ هر چه زودتر ارسال کنید.` : `مهلت ثبت و ارسال: ${fa(cycle.deadlines.allocation.label)}`}
        </p>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <div>
              <CardTitle>سهم هر کسب‌وکار</CardTitle>
              <CardDescription>{STATE_LABEL[state]}</CardDescription>
            </div>
            {editable && previous && (
              <Button variant="outline" size="sm" onClick={() => setValues(Object.fromEntries(previous.lines.map((line) => [line.businessUnitId, String(+(Number(line.percentage) * 100).toFixed(2))])))}>
                <Copy className="size-4" /> کپی از {formatMonthYear(previous.jalaliYear, previous.jalaliMonth)}
              </Button>
            )}
          </CardHeader>
          <CardContent className="grid gap-4">
            {!unitsOnly && <div>
              <div className="mb-1 flex items-center justify-between text-sm">
                <span>جمع</span>
                <span className={cn("font-semibold tabular-nums", complete ? "text-success" : total > 100 ? "text-destructive" : "")}>{total.toLocaleString("fa-IR", { maximumFractionDigits: 2 })}٪ از ۱۰۰٪</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(total)} aria-label="جمع درصدها">
                <div className={cn("h-full rounded-full transition-all motion-reduce:transition-none", complete ? "bg-success" : total > 100 ? "bg-destructive" : "bg-primary")} style={{ width: `${Math.min(total, 100)}%` }} />
              </div>
            </div>}
            <div className="grid gap-2">
              {organization.data.businessUnits.filter((unit) => !unitsOnly || data.lines.some((line) => line.businessUnitId === unit.id)).map((unit) => (
                <div key={unit.id} className="grid grid-cols-[1fr_110px] items-center gap-3 rounded-lg border px-3 py-2">
                  <label htmlFor={`bu-${unit.id}`} className="text-sm">{unit.nameFa}</label>
                  <div className="relative">
                    <Input id={`bu-${unit.id}`} disabled={!editable} inputMode="decimal" dir="ltr" className="pe-7 text-left tabular-nums disabled:opacity-100"
                      value={values[unit.id] ?? ""} placeholder="0"
                      onChange={(event) => setValues((old) => ({ ...old, [unit.id]: event.target.value }))} />
                    <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-xs text-muted-foreground">٪</span>
                  </div>
                </div>
              ))}
            </div>
            <Field>
              <FieldLabel htmlFor="notes">توضیحات (اختیاری)</FieldLabel>
              <Textarea id="notes" disabled={!editable} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </Field>
            {save.error && <FieldError>{messageFor(save.error)}</FieldError>}
            {editable && (
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={() => save.mutate(false)} disabled={save.isPending}>ذخیره پیش‌نویس</Button>
                <Button onClick={() => save.mutate(true)} disabled={!complete || save.isPending}><Send className="size-4" /> {state === "rejected" ? "ارسال مجدد" : "ارسال برای تأیید"}</Button>
              </div>
            )}
            {editable && !complete && total > 0 && <p className="text-xs text-muted-foreground">برای ارسال، جمع باید دقیقاً ۱۰۰٪ باشد ({(100 - total).toLocaleString("fa-IR", { maximumFractionDigits: 2 })}٪ باقی مانده).</p>}
          </CardContent>
        </Card>

        <div className="grid gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">مسیر تأیید</CardTitle></CardHeader>
            <CardContent className="grid gap-4">
              <StatusStepper allocation={data} />
              {data.canReturn && (
                <Button variant="outline" className="w-full" onClick={() => setReturning(true)}>
                  <Undo2 className="size-4" aria-hidden /> برگشت به کاربر برای اصلاح
                </Button>
              )}
            </CardContent>
          </Card>
          {returning && <ReturnDialog allocationId={data.id} employee={data.employee.nameFa} onClose={() => setReturning(false)} />}
          {data.history?.length ? (
            <Card>
              <CardHeader><CardTitle className="text-base">تاریخچه</CardTitle></CardHeader>
              <CardContent className="grid gap-2">
                {data.history.map((item) => (
                  <div key={item.id} className="border-b pb-2 text-sm last:border-0 last:pb-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{HISTORY_LABEL[item.action] ?? item.action}{item.businessUnit ? ` — ${item.businessUnit.nameFa}` : ""}</span>
                      <span className="text-xs text-muted-foreground">{new Date(item.timestamp).toLocaleDateString("fa-IR")}</span>
                    </div>
                    {item.actor && <p className="text-xs text-muted-foreground">{item.actor.nameFa}</p>}
                    {item.notes && <p className="mt-1 text-xs">{item.notes}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null}
          {owner && state === "approved" && <Badge className="w-fit bg-success">تأیید نهایی شد</Badge>}
        </div>
      </div>
    </Page>
  )
}

const toLatin = (value: string) => value.replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit))).replace(/[٫]/g, ".").trim()
